import Delete from '@mui/icons-material/Delete'
import LocalOffer from '@mui/icons-material/LocalOffer'
import { alpha, Box, Grid, IconButton, Typography } from '@mui/material'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { isEmpty } from 'lodash-es'
import { useState } from 'react'

interface DiscountTagProps {
    name: string
    owner?: string
    discountAmount: number
    isSelected?: boolean
    onClick?: () => void
    margin?: number
    fontSize?: number
    note?: string
    showDiscountValue?: boolean
    onRemove?: () => void
    noPaddingRight?: boolean
    disableEditOrder?: boolean
}

export function EntireDiscountTag(props: DiscountTagProps) {
    const { isMobile } = useScreenSize()
    const [isHover, setIsHover] = useState(false)
    const {
        name,
        discountAmount,
        isSelected = false,
        onClick,
        fontSize = 14,
        note = undefined,
        owner = undefined,
        showDiscountValue = true,
        onRemove,
        disableEditOrder,
    } = props

    return (
        <Box
            py="5px"
            px={1}
            mb="5px"
            sx={
                isMobile
                    ? {}
                    : {
                          cursor: onClick ? 'pointer' : undefined,
                          backgroundColor: (theme) =>
                              isSelected
                                  ? theme.palette.default.main
                                  : isHover
                                  ? alpha(theme.palette.default.main, 0.5)
                                  : undefined,
                          color: (theme) =>
                              isSelected
                                  ? theme.palette.default.contrastText
                                  : theme.palette.primary.main,
                          borderLeft: 4,
                          borderLeftColor: isSelected
                              ? 'default.contrastText'
                              : 'primary.main',
                          fontWeight: isSelected ? 600 : undefined,
                      }
            }
            onClick={(e) => {
                if (onClick) {
                    e.stopPropagation()
                    onClick()
                }
            }}
            onMouseEnter={() => {
                if (onClick) setIsHover(true)
            }}
            onMouseLeave={() => {
                if (onClick) setIsHover(false)
            }}
        >
            <Grid container item>
                <Box fontSize={fontSize}>
                    <div
                        style={{
                            display: 'flex',
                        }}
                    >
                        <LocalOffer
                            style={{
                                width: fontSize,
                                marginRight: 4,
                            }}
                        />
                        {`${name}${owner ? `: ${owner}` : ''}`}
                    </div>
                </Box>
                {onRemove && isMobile && (
                    <IconButton
                        disabled={disableEditOrder}
                        onClick={onRemove}
                        sx={{ p: 0, pl: 1 }}
                    >
                        <Delete sx={{ mt: '-3px' }} />
                    </IconButton>
                )}
                <Box flex="1 1 auto" />
                <Box>
                    {showDiscountValue && (
                        <MonetaryFormat
                            forceMonetaryFormat="minus"
                            value={discountAmount}
                            fontSize={fontSize}
                            color={(theme) =>
                                isSelected
                                    ? theme.palette.default.contrastText
                                    : undefined
                            }
                        />
                    )}
                </Box>
            </Grid>
            {note && !isEmpty(note) && (
                <Box>
                    <Typography
                        color={(theme) =>
                            isSelected
                                ? theme.palette.default.contrastText
                                : theme.palette.text.primary
                        }
                        style={{ fontSize: 14 }}
                    >
                        {note}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

export function ItemDiscountTag(props: DiscountTagProps) {
    const {
        name,
        discountAmount,
        isSelected = false,
        onClick,
        margin = 4,
        fontSize = 14,
        note = undefined,
        showDiscountValue = true,
        onRemove,
        noPaddingRight = false,
        disableEditOrder,
    } = props
    const { isMobile } = useScreenSize()
    const [isHover, setIsHover] = useState(false)
    return (
        <Box
            paddingLeft={margin < 2 ? margin : margin + 1}
            paddingRight={margin < 1 || noPaddingRight ? 0 : 1}
            sx={{
                width: '100%',
            }}
        >
            <Grid
                container
                justifyContent={'space-between'}
                px={!noPaddingRight || isMobile ? 1 : 0}
                sx={{
                    backgroundColor: (theme) =>
                        isSelected
                            ? theme.palette.default.main
                            : isHover && !isMobile && Boolean(onClick)
                            ? alpha(theme.palette.default.main, 0.5)
                            : undefined,
                    color: (theme) =>
                        isSelected
                            ? theme.palette.default.contrastText
                            : theme.palette.primary.main,
                    cursor:
                        !isMobile && Boolean(onClick) ? 'pointer' : undefined,
                }}
                onClick={(e) => {
                    if (onClick) {
                        e.stopPropagation()
                        onClick()
                    }
                }}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                <Box width={isMobile ? 180 : undefined} fontSize={fontSize}>
                    <div
                        style={{
                            display: 'flex',
                        }}
                    >
                        <LocalOffer
                            style={{
                                width: fontSize,
                                marginRight: 4,
                            }}
                        />
                        {name}
                        {onRemove && !disableEditOrder && (
                            <IconButton onClick={onRemove} sx={{ p: 0, pl: 1 }}>
                                <Delete sx={{ mt: '-3px' }} />
                            </IconButton>
                        )}
                    </div>
                </Box>
                {!isMobile && <Box flex="1 1 auto" />}
                <Box>
                    {showDiscountValue && (
                        <MonetaryFormat
                            forceMonetaryFormat="minus"
                            value={discountAmount}
                            fontSize={fontSize}
                            color={(theme) =>
                                isSelected
                                    ? theme.palette.default.contrastText
                                    : undefined
                            }
                        />
                    )}
                </Box>
            </Grid>
            {note && !isEmpty(note) && (
                <Box sx={{ px: 1 }}>
                    <Typography
                        color={(theme) =>
                            isSelected || (isHover && Boolean(onClick))
                                ? theme.palette.default.contrastText
                                : theme.palette.text.primary
                        }
                        variant="caption"
                    >
                        {note}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}
